export const SCREENSHOT_HIDE_ATTR = "data-screenshot-hide";

// Radix dialog/popover overlays mount as direct body children via portals.
// Excluding them prevents a tinted overlay in the capture.
const isRadixPortalLayer = (node: Element): boolean =>
  (node.parentElement === document.body && node.hasAttribute("data-state")) || node.getAttribute("role") === "dialog";

const isCrossOriginImage = (node: Element): boolean => {
  if (node.tagName !== "IMG") return false;
  const src = (node as HTMLImageElement).src;
  if (!src || src.startsWith("data:") || src.startsWith("blob:")) return false;
  try {
    return new URL(src).origin !== window.location.origin;
  } catch {
    return false;
  }
};

// Transparent 1x1 PNG. Substitutes for any image html-to-image can't fetch so
// a single cross-origin failure doesn't taint the canvas and break toBlob.
const TRANSPARENT_PIXEL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR4nGNgYGBgAAAABQABh6FO1AAAAABJRU5ErkJggg==";

/**
 * Capture a PNG screenshot of the current page. Returns null on failure
 * (treat as best-effort). `html-to-image` is dynamically imported so the
 * library only ships to users who actually trigger a report.
 */
export const capturePageScreenshot = async (): Promise<Blob | null> => {
  try {
    const { toBlob } = await import("html-to-image");
    return await toBlob(document.body, {
      filter: (node) => {
        if (!(node instanceof Element)) return true;
        if (node.hasAttribute(SCREENSHOT_HIDE_ATTR)) return false;
        if (isRadixPortalLayer(node)) return false;
        if (isCrossOriginImage(node)) return false;
        return true;
      },
      imagePlaceholder: TRANSPARENT_PIXEL,
      // `cache: "no-store"` bypasses the browser HTTP cache without altering
      // the URL (S3 presigned signatures stay intact).
      fetchRequestInit: { mode: "cors", credentials: "omit", cache: "no-store" },
      // html-to-image's resource cache strips query params by default, which
      // collapses every Next-proxied image (`/_next/image?url=...`) to the
      // same key and serves the first capture's bytes to all later ones.
      includeQueryParams: true,
    });
  } catch (err) {
    console.error("[screenshot] Capture failed:", err);
    return null;
  }
};
